import PersonIcon from '@mui/icons-material/Person';
import { MenuItem, Stack, TextField, Typography } from '@mui/material';
import { EnhancedDialog } from 'components/enhanced/dialog/EnhancedDialog';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChoiceOption } from 'shared/types/general';

// 1. Modello dati del Form centralizzato
export interface ActivityFormData {
  type: string;
  date: string;
  validationDate: string;
  description: string;
  notes: string;
  operatorName?: string;
  operatorRole?: string;
}

interface ActivityFormDialogProps {
  isCreation?: boolean;
  open: boolean;
  onClose: () => void;
  onSave: (data: ActivityFormData) => void | Promise<void>;
  title: string;
  subtitle: string;
  isLoading?: boolean;
  formData: ActivityFormData;
  // Handler centralizzato per mutazioni atomiche dello stato
  onFieldChange: (field: keyof ActivityFormData, value: string) => void;
  typeChoices: ChoiceOption[];
}

export const ActivityFormDialog = ({
  isCreation = true,
  open,
  onClose,
  onSave,
  title,
  subtitle,
  isLoading = false,
  formData,
  onFieldChange,
  typeChoices
}: ActivityFormDialogProps) => {
  const { t } = useTranslation();
  const translationPrefix = 'pages.equipment.logbook.activities';

  // Props condivise per i TextField MUI (DRY Principle)
  const textFieldProps = {
    fullWidth: true,
    size: 'small' as const,
    InputProps: { sx: { borderRadius: 1.5 } }
  };

  // Handler centralizzato per gli eventi di input standard
  const handleChange =
    (field: keyof ActivityFormData) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onFieldChange(field, e.target.value);
    };

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <EnhancedDialog
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={title}
      subTitle={subtitle}
      isSubmitting={isLoading}
      submitLabel={t('actions.save', 'Salva')}
      cancelLabel={t('actions.reset', 'Annulla')}
      maxWidth="sm">
      <Stack spacing={3} sx={{ pt: 1 }}>
        {/* Info Operatore visibile solo in modalità Edit */}
        {!isCreation && formData.operatorName && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: -0.5 }}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {t(`${translationPrefix}.registered_by`, 'Registrato da')}: {formData.operatorName}
              {formData.operatorRole ? ` (${formData.operatorRole})` : ''}
            </Typography>
          </Stack>
        )}

        {/* Tipologia Attività */}
        <TextField
          select
          label={t(`${translationPrefix}.activity_type`, 'Tipologia Attività')}
          value={formData.type}
          onChange={handleChange('type')}
          {...textFieldProps}>
          {typeChoices.map((choice) => (
            <MenuItem key={choice.value} value={choice.value}>
              {choice.display_name}
            </MenuItem>
          ))}
        </TextField>

        {/* Campi Data Affiancati */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
          <TextField
            type="date"
            disabled={!isCreation}
            label={t(`${translationPrefix}.activity_date`, 'Data Attività')}
            value={formData.date}
            onChange={handleChange('date')}
            {...textFieldProps}
          />
          <TextField
            type="date"
            label={t(`${translationPrefix}.validation_date`, 'Data Validazione')}
            value={formData.validationDate}
            onChange={handleChange('validationDate')}
            {...textFieldProps}
          />
        </Stack>

        {/* Descrizione */}
        <TextField
          label={t(`${translationPrefix}.description`, 'Descrizione')}
          placeholder={t(
            `${translationPrefix}.description_placeholder`,
            "Inserisci i dettagli dell'attività..."
          )}
          multiline
          rows={3}
          value={formData.description}
          onChange={handleChange('description')}
          {...textFieldProps}
        />

        {/* Note */}
        <TextField
          label={t(`${translationPrefix}.notes`, 'Note')}
          placeholder={t(
            `${translationPrefix}.notes_placeholder`,
            'Aggiungi eventuali note o promemoria...'
          )}
          multiline
          rows={2}
          value={formData.notes}
          onChange={handleChange('notes')}
          {...textFieldProps}
        />
      </Stack>
    </EnhancedDialog>
  );
};
